import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Section } from '@/components/Section';
import { events, track } from '@/lib/analytics';
import { validateMembershipCode, type MembershipCodeResult } from '@/lib/api';
import { JoinForm } from './join/JoinForm';
import { NotFoundPage } from './NotFoundPage';

type GateState =
  | { kind: 'loading' }
  | { kind: 'valid'; data: MembershipCodeResult; checkoutCode?: string }
  | { kind: 'invalid' };

const CANONICAL_MEMBERSHIP_CODES: Record<
  string,
  { data: MembershipCodeResult; checkoutCode?: string }
> = {
  new: { data: { valid: true, athletes: false } },
  experienced: { data: { valid: true, athletes: true }, checkoutCode: 'DISC-2026' },
};

export function JoinPage() {
  const [params] = useSearchParams();
  const code = params.get('code') ?? '';
  const [gate, setGate] = useState<GateState>({ kind: 'loading' });

  useEffect(() => {
    track(events.joinView);
  }, []);

  useEffect(() => {
    if (!code) {
      setGate({ kind: 'invalid' });
      return;
    }
    const canonical = CANONICAL_MEMBERSHIP_CODES[code];
    if (canonical) {
      setGate({ kind: 'valid', ...canonical });
      return;
    }
    let cancelled = false;
    validateMembershipCode(code)
      .then((res) => {
        if (cancelled) return;
        if (res === null) setGate({ kind: 'invalid' });
        else setGate({ kind: 'valid', data: res });
      })
      .catch(() => {
        if (!cancelled) setGate({ kind: 'invalid' });
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (gate.kind === 'invalid') return <NotFoundPage />;
  if (gate.kind === 'loading') {
    return (
      <Section eyebrow="Membership" title="…">
        <p className="text-sm text-bone/60" data-testid="join-loading">
          Verifying your invite…
        </p>
      </Section>
    );
  }

  return (
    <Section
      eyebrow="Membership"
      title={`Pick your programs - ${gate.data.athletes ? 'Athlete' : 'Standard'} Pricing`}
    >
      {/* Stated before checkout so the cancellation terms are never a surprise. */}
      <p className="mb-6 max-w-2xl text-sm text-bone/60">
        Memberships run on a 30-day notice cancel policy — full terms are shown during checkout.
      </p>
      <JoinForm gateAthletes={gate.data.athletes} checkoutCode={gate.checkoutCode} />
    </Section>
  );
}
